import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import {
  getCompanySettingsApi,
  getFuneralDetailAPI,
  getFuneralListAPI,
  getVigilListAPI,
} from "services/apis/funeral";

export const getFuneralList = createAsyncThunk(
  "funeral/GET_FUNERALS",
  async (params, { rejectWithValue }) => {
    try {
      const response = await getFuneralListAPI(params);
      return response;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const getVigilList = createAsyncThunk(
  "funeral/GET_VIGILS",
  async (params, { rejectWithValue }) => {
    try {
      const response = await getVigilListAPI(params);
      return response;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const getFuneralDetail = createAsyncThunk(
  "funeral/GET_FUNERAL",
  async (params, { rejectWithValue }) => {
    try {
      const response = await getFuneralDetailAPI(params);

      return response;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const updateFuneralInList = createAsyncThunk(
  "funeral/UPDATE_FUNERAL_IN_LIST",
  async (updatedFuneral, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const updatedList = state.funeral.funeralList.map(funeral =>
        funeral.id === updatedFuneral.id ? updatedFuneral : funeral
      );
      return updatedList;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const getCompanySettings = createAsyncThunk(
  "funeral/GET_COMPANY_SETTINGS",
  async (companyId, { rejectWithValue }) => {
    try {
      const response = await getCompanySettingsApi(companyId);
      return response;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

const initialState = {
  type: "", // 通夜(EVENT_TYPE_VIGIL='vigil')、葬儀(EVENT_TYPE_FUNERAL='funeral')の判定フラグ
  companyId: "",
  companySettings: {
    // 領収書印刷機能
    receiptFuncFlag: {
      isAvailable: true, // adminの管理画面で設定する使用可否フラグ
      isActivated: true, // 葬儀社設定画面で設定する使用フラグ
      isReceiptNeeded: false, // 受付時に参列者が設定する要否フラグe
    },
    // 供花供物手動登録機能
    enableItemRecordBook: false,
    // その他の葬儀社設定
    enableFuneralGiftPopup: false,
    enableDownloadAttendeeUserListCSV: false,
    enableSnsSharing: false,
    enableCondolenceGiftSymbol: false,
    enableExportAttendeeExcel: false,
    enableMemorialLedger: false,
    enableTempleSelection: false,
  },
  funeralList: [],
  vigilList: [],
  currentFuneral: null,
  loading: false,
  error: null,
};

const funeralSlice = createSlice({
  name: "funeral",
  initialState,

  reducers: {
    resetState: (state, action) => {
      const fieldName = action.payload;
      state[fieldName] = initialState[fieldName];
    },
    setCurrentFuneral: (state, action) => {
      return {
        ...state,
        currentFuneral: action.payload,
      };
    },
    setCurrentType: (state, action) => {
      return {
        ...state,
        type: action.payload,
      };
    },
    resetCurrentFuneral: state => {
      state.currentFuneral = null;
    },
    actionSetIsReceiptNeeded: (state, action) => {
      state.companySettings.receiptFuncFlag.isReceiptNeeded = action.payload;
    },
  },

  extraReducers: builder => {
    builder
      .addCase(getFuneralList.pending, state => {
        state.funeralList = [];
        state.loading = true;
      })
      .addCase(getFuneralList.rejected, (state, action) => {
        state.loading = false;
        state.error = action?.payload?.errors;
      })
      .addCase(getFuneralList.fulfilled, (state, action) => {
        state.loading = false;
        state.funeralList = action.payload || [];
      })
      .addCase(getVigilList.pending, state => {
        state.vigilList = [];
        state.loading = true;
      })
      .addCase(getVigilList.rejected, (state, action) => {
        state.loading = false;
        state.error = action?.payload?.errors;
      })
      .addCase(getVigilList.fulfilled, (state, action) => {
        state.loading = false;
        state.vigilList = action.payload || [];
      })
      .addCase(getFuneralDetail.pending, state => {
        state.loading = true;
      })
      .addCase(getFuneralDetail.rejected, (state, action) => {
        state.loading = false;
        state.error = action?.payload?.errors;
      })
      .addCase(getFuneralDetail.fulfilled, (state, action) => {
        state.loading = false;

        const printerSettingObj = action.payload.printer;
        const canPrintAttendeeCard = Object.keys(printerSettingObj).some(
          printerMailAddress => printerSettingObj[printerMailAddress].isAttendeeCard === true
        );
        const canPrintReceipt = Object.keys(printerSettingObj).some(
          printerMailAddress => printerSettingObj[printerMailAddress].isReceipt === true
        );
        const canPrint = canPrintAttendeeCard || canPrintReceipt;

        state.currentFuneral =
          {
            ...action.payload,
            canPrint,
            canPrintAttendeeCard,
            canPrintReceipt,
          } || [];
      })
      .addCase(updateFuneralInList.fulfilled, (state, action) => {
        state.funeralList = action.payload;
      })
      .addCase(getCompanySettings.pending, state => {
        state.loading = true;
      })
      .addCase(getCompanySettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action?.payload?.errors;
      })
      .addCase(getCompanySettings.fulfilled, (state, action) => {
        state.loading = false;

        state.companySettings = {
          ...initialState.companySettings, // デフォルト値を保持
          ...action.payload, // APIレスポンスで上書き
          // receiptFuncFlagは個別に処理
          receiptFuncFlag: {
            ...initialState.companySettings.receiptFuncFlag,
            isAvailable: action.payload?.useReceptionReceipt ?? false, // 管理者が設定する使用可否フラグ
            isActivated: action.payload?.enableReceptionReceipt ?? false, // 葬儀社が設定する使用フラグ
            isReceiptNeeded: false, // 受付時に参列者が設定する要否フラグ（葬儀社設定の取得、反映時点では常にfalse）
          },
        };
      });
  },
});

export const {
  resetState,
  setCurrentFuneral,
  resetCurrentFuneral,
  setCurrentType,
  actionSetIsReceiptNeeded,
} = funeralSlice.actions;
const { reducer: funeralReducer } = funeralSlice;
export default funeralReducer;
